import { useEffect } from 'react';

const ZSL_BANNER_EVENT = 'zsl200:show-banner';
const ZSL_BANNER_SEEN_COOKIE = 'zsl200_banner_seen';
const ZSL_BANNER_DEBUG_COOKIE = 'zsl200_banner_debug';
const ZSL_BANNER_COOKIE_EXPIRY = 'Wed, 06 May 2026 00:00:00 GMT';
const CAMPAIGN_START = new Date('2026-04-28T23:00:00Z');
const CAMPAIGN_END = new Date('2026-05-05T22:59:59Z');

type OneTrustWindow = Window & {
  OneTrust?: { IsAlertBoxClosed?: () => boolean };
  OptanonWrapper?: () => void;
};

const getDebugDateRange = (): { start: Date; end: Date } | null => {
  const match = document.cookie.match(
    new RegExp(`${ZSL_BANNER_DEBUG_COOKIE}=(\\d{4}-\\d{2}-\\d{2})--(\\d{4}-\\d{2}-\\d{2})`)
  );
  if (!match) return null;
  return {
    start: new Date(`${match[1]}T00:00:00Z`),
    end: new Date(`${match[2]}T23:59:59Z`),
  };
};

const isWithinWindow = (start: Date, end: Date) => {
  const now = new Date();
  return now >= start && now <= end;
};

export const isBannerSeen = (cookieHeader: string): boolean => {
  const cookies = cookieHeader.split(';').map((c) => c.trim());
  if (cookies.some((c) => c.startsWith(`${ZSL_BANNER_DEBUG_COOKIE}=`))) return false;
  return cookies.some((c) => c === `${ZSL_BANNER_SEEN_COOKIE}=1`);
};

export const useCookieProBannerTrigger = (cookieProDataDomain: string, theme: string): void => {
  useEffect(() => {
    if (!cookieProDataDomain || theme !== 'zsl') return;

    const debugRange = getDebugDateRange();
    const start = debugRange?.start ?? CAMPAIGN_START;
    const end = debugRange?.end ?? CAMPAIGN_END;

    if (!isWithinWindow(start, end)) return;
    if (!debugRange && document.cookie.includes(`${ZSL_BANNER_SEEN_COOKIE}=1`)) return;

    let fired = false;
    const globalWindow = window as OneTrustWindow;
    const previousOptanonWrapper = globalWindow.OptanonWrapper;

    const fire = () => {
      if (fired) return;
      fired = true;
      if (!debugRange) {
        document.cookie = `${ZSL_BANNER_SEEN_COOKIE}=1; path=/; expires=${ZSL_BANNER_COOKIE_EXPIRY}; SameSite=Lax`;
      }
      window.dispatchEvent(new CustomEvent(ZSL_BANNER_EVENT));
    };

    window.addEventListener('OTConsentApplied', fire);

    globalWindow.OptanonWrapper = () => {
      previousOptanonWrapper?.();
      if (globalWindow.OneTrust?.IsAlertBoxClosed?.()) fire();
    };

    return () => {
      window.removeEventListener('OTConsentApplied', fire);
      globalWindow.OptanonWrapper = previousOptanonWrapper;
    };
  }, [cookieProDataDomain, theme]);
};
